import { cn } from '@amore/ui/cn'
import { Button } from '@amore/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@amore/ui/components/dropdown-menu'
import { Input } from '@amore/ui/components/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@amore/ui/components/tooltip'
import {
  Baby,
  BedDouble,
  Car,
  Check,
  Clock3,
  Globe,
  type LucideIcon,
  Pencil,
  Plus,
  Trash2,
  UserRoundPlus,
  X,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { formatDate, newId, plural } from '@/lib/format'
import { circleLabels, dietLabels } from '@/lib/labels'
import { replaceById, useCanEdit, useUpdate, useWedding } from '@/mock/store'
import type { Attendance, Diet, GuestCircle, Party, Person } from '@/mock/types'
import { Segmented } from '@/patterns/detail'
import { ConfirmDialog, DetailSheet } from '@/patterns/dialog'
import { useToast } from '@/shell/toaster'
import { isPending, partyTitle, personName } from './model'

type Draft = Omit<Party, 'id' | 'repliedAt' | 'message'>

const emptyPerson = (): Person => ({
  id: newId('guest'),
  firstName: '',
  lastName: '',
  attendance: 'invited',
  child: false,
  diets: [],
})

const emptyDraft = (parts: string[]): Draft => ({
  circle: 'shared',
  persons: [emptyPerson()],
  parts,
  replied: false,
  accommodation: false,
  transport: false,
})

const draftOf = ({ id: _id, repliedAt: _at, message: _m, ...draft }: Party): Draft => draft

const circles = (['bride', 'groom', 'shared'] as GuestCircle[]).map((value) => ({
  value,
  label: circleLabels[value],
}))
const diets = Object.keys(dietLabels) as Diet[]

const hasName = (person: Person) => Boolean(person.firstName.trim() || person.lastName.trim())
const field = 'h-10 border-transparent bg-muted/60 hover:bg-muted focus:bg-card'

/**
 * One sheet for adding and editing an invitation. The couple decides who is
 * invited (names, how many companions, whose side, day or evening); the guests
 * answer the rest through the wedding website (attendance, companion names,
 * diets, accommodation, transport). The couple can fill the answers in for
 * them, e.g. after a phone call.
 */
export function PartySheet({ party, onClose }: { party?: Party; onClose: () => void }) {
  const isNew = !party
  const canEdit = useCanEdit()
  const update = useUpdate()
  const { dayParts } = useWedding()
  const { showToast } = useToast()
  const [initial] = useState(() =>
    party ? draftOf(party) : emptyDraft(dayParts.map((part) => part.id)),
  )
  const [partsError, setPartsError] = useState(false)
  const [draft, setDraft] = useState(initial)
  const [answering, setAnswering] = useState(false)
  const [nameError, setNameError] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [focusId, setFocusId] = useState<string | null>(null)
  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }))
  const setPerson = (id: string, patch: Partial<Person>) => {
    setNameError(false)
    set({ persons: replaceById(draft.persons, id, patch) })
  }
  const addPerson = (plusOne: boolean) => {
    const person = { ...emptyPerson(), ...(plusOne ? { plusOne: true } : {}) }
    set({ persons: [...draft.persons, person] })
    if (!plusOne) setFocusId(person.id)
  }
  const removePerson = (id: string) =>
    set({ persons: draft.persons.filter((person) => person.id !== id) })

  const preview = { id: 'preview', ...draft }
  const title = draft.persons.some(hasName) || draft.name ? partyTitle(preview) : ''
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial)

  const save = () => {
    const persons = draft.persons
      .filter((person) => person.plusOne || hasName(person))
      .map((person) => ({
        ...person,
        firstName: person.firstName.trim(),
        lastName: person.lastName.trim(),
      }))
    const missingName = !persons.some(hasName)
    const missingParts = draft.parts.length === 0
    setNameError(missingName)
    setPartsError(missingParts)
    if (missingName || missingParts) return
    const saved = {
      ...draft,
      persons,
      name: draft.name?.trim() || undefined,
      replied: persons.some((person) => !isPending(person)),
    }
    update((current) => ({
      ...current,
      parties: isNew
        ? [...current.parties, { id: newId('party'), ...saved }]
        : replaceById(current.parties, party.id, saved),
    }))
    showToast({
      title: isNew ? 'Pozvánka přidána' : 'Pozvánka uložena',
      description: partyTitle({ id: 'saved', ...saved }),
      tone: 'success',
    })
    onClose()
  }
  const remove = () => {
    if (!party) return
    update((current) => ({
      ...current,
      parties: current.parties.filter((item) => item.id !== party.id),
    }))
    showToast({ title: 'Pozvánka smazána', description: partyTitle(party) })
    onClose()
  }

  return (
    <>
      <DetailSheet
        open
        onOpenChange={(open) => {
          if (!open) onClose()
        }}
        eyebrow={isNew ? 'Nová pozvánka' : 'Pozvánka'}
        title={title || 'Nová pozvánka'}
        titleContent={
          <p
            className={cn(
              'font-heading text-[1.75rem] leading-tight tracking-tight',
              !title && 'text-muted-foreground/60',
            )}
          >
            {title || 'Koho zvete?'}
          </p>
        }
        dirty={dirty}
        primaryAction={
          canEdit ? { label: isNew ? 'Přidat pozvánku' : 'Uložit', onClick: save } : undefined
        }
        destructiveAction={
          canEdit && !isNew
            ? {
                label: 'Smazat pozvánku',
                icon: <Trash2 className="size-4" aria-hidden="true" />,
                onClick: () => setConfirmDelete(true),
              }
            : undefined
        }
      >
        <div className="divide-y divide-border [&>*]:py-7 [&>*:first-child]:pt-1 [&>*:last-child]:pb-1">
          <section className="space-y-3">
            <h3 className="text-sm font-semibold">Kdo je pozvaný</h3>
            <fieldset disabled={!canEdit} aria-label="Kdo je pozvaný" className="space-y-2">
              {draft.persons.map((person, index) => (
                <InviteeRow
                  key={person.id}
                  person={person}
                  autoFocus={(isNew && index === 0) || person.id === focusId}
                  canRemove={draft.persons.length > 1}
                  onChange={(patch) => setPerson(person.id, patch)}
                  onRemove={() => removePerson(person.id)}
                />
              ))}
            </fieldset>
            {nameError ? (
              <p className="text-sm text-error-foreground">
                Doplňte jméno alespoň jednomu hostovi.
              </p>
            ) : null}
            {canEdit ? (
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  size="sm"
                  appearance="outline"
                  tone="neutral"
                  onClick={() => addPerson(false)}
                >
                  <Plus aria-hidden="true" className="size-3.5" />
                  Přidat osobu
                </Button>
                <Button
                  size="sm"
                  appearance="outline"
                  tone="neutral"
                  onClick={() => addPerson(true)}
                >
                  <UserRoundPlus aria-hidden="true" className="size-3.5" />
                  Přidat doprovod
                </Button>
              </div>
            ) : null}
          </section>

          <section className="space-y-5">
            <h3 className="text-sm font-semibold">Pozvánka</h3>
            <fieldset
              disabled={!canEdit}
              aria-label="Pozvánka"
              className="grid grid-cols-2 gap-x-6 gap-y-7"
            >
              <Tile label="Za koho" wide>
                <Segmented
                  label="Za koho"
                  options={circles}
                  value={draft.circle}
                  onChange={(circle) => set({ circle })}
                  disabled={!canEdit}
                />
              </Tile>
              <Tile label="Pozvání" wide>
                <div className="flex flex-wrap gap-2">
                  {dayParts.map((part) => {
                    const pressed = draft.parts.includes(part.id)
                    return (
                      <Chip
                        key={part.id}
                        pressed={pressed}
                        onClick={() => {
                          setPartsError(false)
                          set({
                            parts: pressed
                              ? draft.parts.filter((id) => id !== part.id)
                              : dayParts
                                  .map((item) => item.id)
                                  .filter((id) => id === part.id || draft.parts.includes(id)),
                          })
                        }}
                      >
                        {pressed ? <Check aria-hidden="true" className="size-3.5" /> : null}
                        {part.label}
                        {part.time ? (
                          <span className="font-normal text-muted-foreground">{part.time}</span>
                        ) : null}
                      </Chip>
                    )
                  })}
                </div>
                {partsError ? (
                  <p className="text-sm text-error-foreground">
                    Vyberte, na kterou část dne hosty zvete.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Části dne si upravíte v Nastavení.
                  </p>
                )}
              </Tile>
              <Tile label="Vlastní název" wide>
                <Input
                  value={draft.name ?? ''}
                  maxLength={80}
                  readOnly={!canEdit}
                  placeholder={
                    draft.persons.some(hasName)
                      ? `Jinak se zobrazí „${partyTitle({ ...preview, name: undefined })}“`
                      : 'Např. Kolegyně z práce'
                  }
                  onChange={(event) => set({ name: event.target.value })}
                  className={field}
                />
              </Tile>
            </fieldset>
          </section>

          <section className="space-y-4">
            <div className="flex min-h-9 items-center justify-between gap-3">
              <h3 className="text-sm font-semibold">Odpověď</h3>
              {canEdit && draft.replied && !answering ? (
                <Button
                  size="sm"
                  appearance="ghost"
                  tone="neutral"
                  onClick={() => setAnswering(true)}
                >
                  <Pencil aria-hidden="true" className="size-3.5" />
                  Upravit
                </Button>
              ) : null}
            </div>
            {answering ? (
              <AnswersEditor draft={draft} onPerson={setPerson} onChange={set} />
            ) : draft.replied ? (
              <Answers party={{ ...preview, message: party?.message }} />
            ) : (
              <div className="space-y-3">
                <p className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  <Globe aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                  {isNew
                    ? 'Účast, jména doprovodu, stravu, ubytování a odvoz vyplní hosté sami přes svatební web.'
                    : 'Zatím neodpověděli. Účast, jména doprovodu, stravu, ubytování a odvoz vyplní sami přes svatební web.'}
                </p>
                {canEdit ? (
                  <Button
                    size="sm"
                    appearance="outline"
                    tone="neutral"
                    onClick={() => setAnswering(true)}
                  >
                    Vyplnit za hosty
                  </Button>
                ) : null}
              </div>
            )}
            {party?.repliedAt && !answering ? (
              <p className="text-xs text-muted-foreground">
                Odpověděli přes web {formatDate(party.repliedAt, 'long')}
              </p>
            ) : null}
          </section>
        </div>
      </DetailSheet>
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        heading="Smazat pozvánku?"
        description={
          party && party.persons.length > 1
            ? `Ze seznamu zmizí i ${party.persons.length} ${plural(party.persons.length, 'host', 'hosté', 'hostů')} v ní. Tuto akci nelze vrátit.`
            : 'Tuto akci nelze vrátit.'
        }
        keepLabel="Ponechat pozvánku"
        confirmLabel="Smazat pozvánku"
        onConfirm={remove}
      />
    </>
  )
}

/** Who is invited: a name for each person; a companion is just a placeholder the guest names. */
function InviteeRow({
  person,
  autoFocus,
  canRemove,
  onChange,
  onRemove,
}: {
  person: Person
  autoFocus: boolean
  canRemove: boolean
  onChange: (patch: Partial<Person>) => void
  onRemove: () => void
}) {
  const canEdit = useCanEdit()
  return (
    <div className="flex items-center gap-2">
      {person.plusOne ? (
        <div className="flex h-10 min-w-0 flex-1 items-center gap-2.5 rounded-control border border-dashed border-border px-3.5 text-sm">
          <UserRoundPlus aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
          {hasName(person) ? (
            <span>
              {personName(person)}
              <span className="text-muted-foreground"> · doprovod</span>
            </span>
          ) : (
            <span className="text-muted-foreground">Doprovod · jméno doplní host v odpovědi</span>
          )}
        </div>
      ) : (
        <>
          <Input
            value={person.firstName}
            maxLength={60}
            readOnly={!canEdit}
            data-autofocus={autoFocus || undefined}
            ref={autoFocus ? focusOnMount : undefined}
            aria-label="Jméno"
            placeholder="Jméno"
            onChange={(event) => onChange({ firstName: event.target.value })}
            className={cn(field, 'min-w-0 flex-1')}
          />
          <Input
            value={person.lastName}
            maxLength={60}
            readOnly={!canEdit}
            aria-label="Příjmení"
            placeholder="Příjmení"
            onChange={(event) => onChange({ lastName: event.target.value })}
            className={cn(field, 'min-w-0 flex-1')}
          />
        </>
      )}
      {canEdit && canRemove ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              appearance="ghost"
              tone="neutral"
              size="icon"
              onClick={onRemove}
              className="size-10 shrink-0"
            >
              <X aria-hidden="true" className="size-4" />
              <span className="sr-only">Odebrat z pozvánky</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Odebrat z pozvánky</TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  )
}

// Focus a field added after the sheet opened (the sheet itself focuses `data-autofocus` on open).
const focusOnMount = (element: HTMLInputElement | null) => {
  if (element && document.activeElement?.tagName !== 'INPUT') element.focus()
}

const replies = [
  {
    value: 'confirmed',
    label: 'Přijde',
    icon: Check,
    on: 'bg-success text-success-foreground',
  },
  {
    value: 'pending',
    label: 'Čekáme',
    icon: Clock3,
    on: 'bg-warning text-warning-foreground',
  },
  { value: 'declined', label: 'Nepřijde', icon: X, on: 'bg-danger text-danger-foreground' },
] as const

const replyOf = (person: Person) =>
  replies.find((reply) => reply.value === (isPending(person) ? 'pending' : person.attendance)) ??
  replies[1]

/** The guests' answers as they sent them: each person with their reply and diet, then needs and message. */
function Answers({ party }: { party: Party }) {
  const needs = [party.accommodation && 'ubytování', party.transport && 'odvoz'].filter(Boolean)
  return (
    <div className="space-y-4">
      <ul className="space-y-2.5">
        {party.persons.map((person) => {
          const reply = replyOf(person)
          const Icon = reply.icon
          const details = [
            person.child && 'dítě',
            ...person.diets.map((diet) => dietLabels[diet].toLowerCase()),
          ].filter(Boolean)
          return (
            <li key={person.id} className="flex items-center gap-3 text-sm">
              <span
                className={cn('grid size-6 shrink-0 place-items-center rounded-full', reply.on)}
              >
                <Icon aria-hidden="true" className="size-3.5" strokeWidth={2.5} />
              </span>
              <span className="min-w-0">
                <span className="font-medium">
                  {hasName(person) ? personName(person) : 'Doprovod'}
                </span>
                <span className="text-muted-foreground">
                  {` · ${reply.label.toLowerCase()}`}
                  {details.length ? ` · ${details.join(', ')}` : ''}
                </span>
              </span>
            </li>
          )
        })}
      </ul>
      {needs.length ? (
        <p className="text-sm">
          <span className="text-muted-foreground">Potřebují </span>
          {needs.join(' a ')}
        </p>
      ) : null}
      {party.message ? (
        <p className="text-[15px] leading-relaxed text-foreground/85">„{party.message}“</p>
      ) : null}
    </div>
  )
}

/** Filling the answers in for the guests: per person reply, child and diet; then the invitation's needs. */
function AnswersEditor({
  draft,
  onPerson,
  onChange,
}: {
  draft: Draft
  onPerson: (id: string, patch: Partial<Person>) => void
  onChange: (patch: Partial<Draft>) => void
}) {
  return (
    <div className="space-y-6">
      <div className="divide-y divide-border/70">
        {draft.persons.map((person) => (
          <div key={person.id} className="space-y-2.5 py-4 first:pt-0">
            {person.plusOne ? (
              <Input
                value={person.firstName}
                maxLength={60}
                aria-label="Jméno doprovodu"
                placeholder="Jméno doprovodu"
                onChange={(event) => onPerson(person.id, { firstName: event.target.value })}
                className={cn(field, 'max-w-72')}
              />
            ) : (
              <p className="text-sm font-medium">{personName(person) || 'Bez jména'}</p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <AttendancePicker
                value={person.attendance}
                onChange={(attendance) => onPerson(person.id, { attendance })}
              />
              <span aria-hidden="true" className="mx-1 h-5 w-px bg-border" />
              <Chip
                icon={Baby}
                pressed={person.child}
                onClick={() => onPerson(person.id, { child: !person.child })}
              >
                Dítě
              </Chip>
              <DietPicker
                value={person.diets}
                onChange={(next) => onPerson(person.id, { diets: next })}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="space-y-2.5">
        <span className="block text-sm text-muted-foreground">Potřebují</span>
        <div className="flex flex-wrap gap-2">
          <Chip
            icon={BedDouble}
            pressed={draft.accommodation}
            onClick={() => onChange({ accommodation: !draft.accommodation })}
          >
            Ubytování
          </Chip>
          <Chip
            icon={Car}
            pressed={draft.transport}
            onClick={() => onChange({ transport: !draft.transport })}
          >
            Odvoz
          </Chip>
        </div>
      </div>
    </div>
  )
}

/** Picked diets as removable chips; the rest behind "+ Strava". */
function DietPicker({ value, onChange }: { value: Diet[]; onChange: (value: Diet[]) => void }) {
  return (
    <>
      {value.map((diet) => (
        <Chip key={diet} pressed onClick={() => onChange(value.filter((item) => item !== diet))}>
          {dietLabels[diet]}
          <X aria-hidden="true" className="size-3.5" />
        </Chip>
      ))}
      {value.length < diets.length ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              appearance="outline"
              tone="neutral"
              className="border-dashed text-muted-foreground"
            >
              <Plus aria-hidden="true" className="size-3.5" />
              Strava
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {diets
              .filter((diet) => !value.includes(diet))
              .map((diet) => (
                <DropdownMenuItem key={diet} onSelect={() => onChange([...value, diet])}>
                  {dietLabels[diet]}
                </DropdownMenuItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </>
  )
}

/** The person's reply in the list's colours: green comes, yellow waiting, red won't come. */
function AttendancePicker({
  value,
  onChange,
}: {
  value: Attendance
  onChange: (value: Attendance) => void
}) {
  const current = isPending({ attendance: value } as Person) ? 'pending' : value
  return (
    <fieldset aria-label="Odpověď" className="inline-flex rounded-full bg-muted p-1">
      {replies.map((reply) => {
        const selected = reply.value === current
        const Icon = reply.icon
        return (
          <button
            key={reply.value}
            type="button"
            aria-pressed={selected}
            onClick={() =>
              onChange(
                reply.value === 'pending'
                  ? current === 'pending'
                    ? value
                    : 'invited'
                  : reply.value,
              )
            }
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
              selected ? cn(reply.on, 'shadow-sm') : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon aria-hidden="true" className="size-3.5" strokeWidth={2.5} />
            {reply.label}
          </button>
        )
      })}
    </fieldset>
  )
}

function Chip({
  icon: Icon,
  pressed,
  onClick,
  children,
}: {
  icon?: LucideIcon
  pressed: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <Button size="sm" appearance="outline" tone="neutral" aria-pressed={pressed} onClick={onClick}>
      {Icon ? <Icon aria-hidden="true" className="size-3.5" /> : null}
      {children}
    </Button>
  )
}

function Tile({
  label,
  wide = false,
  children,
}: {
  label: string
  wide?: boolean
  children: ReactNode
}) {
  return (
    <div className={cn('space-y-2.5', wide && 'col-span-2')}>
      <span className="block text-sm text-muted-foreground">{label}</span>
      {children}
    </div>
  )
}
